import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Metric {
  id: string;
  value: string;
  label: string;
  iconName: 'HomeIcon' | 'StarIcon' | 'ShieldCheckIcon' | 'UsersIcon';
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  rating: number;
  imageUrl: string;
  imageAlt: string;
}

const metrics: Metric[] = [
{
  id: 'metric_homes',
  value: '1,200+',
  label: 'Homes Cleaned',
  iconName: 'HomeIcon'
},
{
  id: 'metric_rating',
  value: '4.9/5',
  label: 'Average Rating',
  iconName: 'StarIcon'
},
{
  id: 'metric_insured',
  value: '100%',
  label: 'Insured & Bonded',
  iconName: 'ShieldCheckIcon'
},
{
  id: 'metric_staff',
  value: '25+',
  label: 'Trained Professionals',
  iconName: 'UsersIcon'
}];


const testimonials: Testimonial[] = [
{
  id: 'testimonial_1',
  name: 'Sarah Mitchell',
  role: 'Minneapolis MN',
  content: 'Gelle Cleaning transformed my home! Their team is professional, punctual, and uses eco-friendly products. I love coming home to a spotless house every week.',
  rating: 5,
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6f98db8-1765420267487.png",
  imageAlt: 'Smiling woman with brown hair in casual attire in bright living room'
},
{
  id: 'testimonial_2',
  name: 'Michael Chen',
  role: 'Property Manager',
  content: 'As a property manager, I rely on Gelle Cleaning for move-in/out services. They consistently deliver thorough, high-quality work. Highly recommend!',
  rating: 5,
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_17280f6a4-1763301282153.png",
  imageAlt: 'Professional man in business casual attire with glasses in modern office'
},
{
  id: 'testimonial_3',
  name: 'Emily Rodriguez',
  role: 'Small Business Owner',
  content: "Our office has never looked better! Gelle Cleaning's team comes in after hours and leaves everything pristine. Great value and exceptional service.",
  rating: 5,
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb0ee20c-1763294964632.png",
  imageAlt: 'Professional woman with dark hair in business attire smiling confidently'
},
{
  id: 'testimonial_4',
  name: 'David Anderson',
  role: 'St. Paul MN',
  content: 'After trying several cleaning services, Gelle Cleaning stands out. Their attention to detail is incredible, and they always go the extra mile. Worth every penny!',
  rating: 5,
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1887dc1cb-1763293783349.png",
  imageAlt: 'Friendly man with short hair in casual shirt smiling in modern home'
},
{
  id: 'testimonial_5',
  name: 'Jennifer Thompson',
  role: 'Bloomington MN',
  content: 'I have two kids and three pets, so keeping the house clean was overwhelming. Gelle Cleaning has been a lifesaver! They handle everything with care and professionalism.',
  rating: 5,
  imageUrl: "https://images.unsplash.com/photo-1620602442055-c0f6694d36e2",
  imageAlt: 'Cheerful woman with blonde hair in comfortable home setting'
},
{
  id: 'testimonial_6',
  name: 'Robert Martinez',
  role: 'Edina MN',
  content: 'Exceptional service from start to finish! The booking process was easy, the team arrived on time, and my home sparkled. I\'ve already scheduled my next deep clean.',
  rating: 5,
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_191a43c60-1763294769851.png",
  imageAlt: 'Professional man with gray hair in business casual attire in elegant home'
}];


export default function TrustSection() {
  return (
    <section id="trust" className="w-full bg-secondary/30 py-24 lg:py-32 border-t border-border">
      <div className="container mx-auto px-6 lg:px-12">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* Left: Metrics */}
          <div className="reveal">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-6">
              Trusted by Homeowners<br />
              <span className="text-primary">Twin Cities MN</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-12 max-w-md">
              We've built our reputation on reliability, quality, and customer satisfaction. Every cleaner is background-checked, insured, and trained in eco-friendly practices.
            </p>
            
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 gap-6">
              {metrics.map((metric, index) =>
              <div
                key={metric.id}
                className={`bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 reveal delay-${(index + 1) * 100}`}>
                
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Icon name={metric.iconName} size={24} variant="solid" className="text-primary" />
                  </div>
                  <p className="text-3xl font-bold text-foreground mb-1">{metric.value}</p>
                  <p className="text-sm text-muted-foreground">{metric.label}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Right: Testimonials */}
          <div className="space-y-6 reveal delay-200">
            {testimonials.map((testimonial, index) =>
            <div
              key={testimonial.id}
              className={`bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1.5 hover:border-[#4CAF50] transition-all duration-300 reveal delay-${(index + 3) * 100}`}>
              
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-primary/20">
                    <AppImage
                    src={testimonial.imageUrl}
                    alt={testimonial.imageAlt}
                    className="w-full h-full object-cover" />
                  
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-foreground">{testimonial.name}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(testimonial.rating)].map((_, i) =>
                    <Icon key={`${testimonial.id}_star_${i}`} name="StarIcon" size={16} variant="solid" className="text-primary" />
                    )}
                    </div>
                  </div>
                </div>
                <p className="text-foreground leading-relaxed">
                  "{testimonial.content}"
                </p>
              </div>
            )}
            
            {/* More Reviews CTA */}
            <div className="text-center pt-6">
              <button className="inline-flex items-center gap-2 text-primary font-semibold hover:underline transition-all active:scale-95">
                Read More Reviews
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </button>
            </div>
          </div>
          
        </div>
        
      </div>
    </section>);

}